import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import { setShowManageUserDrawer } from '../../../../store/company/action';
import { fetchOwners, GetOwnerDataPayload, setDealDrawer, updateCompany } from '../../../../store/investor/action';
import { GREY_DROPDOWN_CARET } from '../../../../assets/hostedassets';
import ActionMenu from '../../../../common/ActionMenu/ActionMenu';
import StatusDropdown, { StatusDropdownOption } from '../../../../common/_custom/StatusDropdown/StatusDropdown';
import { OrgBasedUser, OrgBasedUsersTableProps, OrgUser } from './OrgBasedUsersTable';
import styles from '../ManageUserTable/ManageUserTable.module.scss';
import { COLORS } from '../../../../utils/constants/colors';
import { fetchCountryCodes } from '../../../../store/company/apiHelpers';
import { closeCommentsThread, setShowCommentsThread } from '../../../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../../../store/commentsThread/commentsThread_reducer';
import { truncateTextForComments } from '../../../../utils/utils';
import dayjs from 'dayjs';
import { removeTooltipContent, setTooltipContent } from '../../../../store/tooltip/tooltip_actions';
import { isEmpty } from 'lodash';
import { ORG_USERS_TABLE, PROFILE_LABEL } from '../../../../common/ManageDealDrawer/DealDrawerConstants';
import { USER_TYPE } from '../../../../utils/constants/user';
import { MANAGE_USERS_TABS } from '../../../../store/investor/constants';
import { DataTableColumn, DataTableColumnType } from '../../../../common/_custom/DataTable/DataTable.types';
import { getVisibleColumns } from '../../../../common/_custom/DataTable/utils';
import { getDealFormConfig, fetchSectorOptions } from '../../../../store/investor/apiHelpers';
import { VIEW_CONFIGS } from '../../../../utils/constants/commonConstants';

export const useOrgBasedUsersTable = ({
    isRowsLoading,
    isSummonUser,
    actionMenuItems,
    category,
    actionConfig,
    fetchRows,
    onFiltersApply,
}: OrgBasedUsersTableProps) => {
    const dispatch = useDispatch();
    const manageUserDrawerState = useAppSelector((state) => state.company.showManageUser);
    const user = useAppSelector((state) => state.user);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const showDealDrawer = useAppSelector((state) => state.investor.dealDrawer.open);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const isFilterDrawerOpen = useAppSelector((state) => state.investor.customFilterDrawer.open);

    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const userData = useAppSelector((state) => state.user.userData);

    const [selectedOrgUsers, setSelectedOrgUsers] = useState<OrgUser[]>([]);
    const [params, setParams] = useSearchParams();
    const [ownerDropdownData, setOwnerDropdownData] = useState({});
    const [localOwners, setLocalOwners] = useState<Record<string, string>>({});
    const [selectedOrgName, setSelectedOrgName] = useState<string>('');
    const [countryCodes, setCountryCodes] = useState<any[]>([]);
    const [clearedOrganizations, setClearedOrganizations] = useState<Set<string>>(new Set());
    const [sectorDropdownOptions, setSectorDropdownOptions] = useState<{ value: string; label: string }[]>([]);
    const [managePartnersColumn, setManagePartnersColumn] = useState<{
        columns: DataTableColumn[];
        pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    }>({ columns: [] });
    const [manageLendersColumn, setManageLendersColumn] = useState<{
        columns: DataTableColumn[];
        pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    }>({ columns: [] });

    const originalCategory = params.get('category');

    const handleManageUsers = (users: OrgUser[], orgId: string, orgName: string) => {
        const usersWithCallingCode = users.map((user) => ({
            ...user,
            mobileNumber:
                user.countryCode && user.mobileNumber && countryCodes.find((cc) => cc.countryCode === user.countryCode)?.callingCode
                    ? `+${countryCodes.find((cc) => cc.countryCode === user.countryCode)?.callingCode} ${user.mobileNumber}`
                    : user.mobileNumber,
        }));

        setSelectedOrgUsers(usersWithCallingCode);
        setSelectedOrgName(orgName);
        dispatch(setShowManageUserDrawer(true));
        params.set('orgId', orgId);
        setParams(params);
    };

    const handleRowClick = (column: any, row: any, event?: any) => {
        const field = column.field || column.headerLabel;

        switch (field) {
            case 'userData':
                handleManageUsers(row.userData, row.organizationId, row.organizationDisplayName || row.organizationName);
                break;

            default:
                params.set('orgId', row.organizationId);
                setParams(params);
                dispatch(
                    setDealDrawer({
                        open: true,
                        drawerLabel: PROFILE_LABEL,
                        drawerSource: ORG_USERS_TABLE,
                        companyName: row.organizationDisplayName ? row.organizationDisplayName : row.organizationName,
                        ...(originalCategory === MANAGE_USERS_TABS.PARTNER && { userType: USER_TYPE.PARTNER }),
                    }),
                );
                break;
        }
    };

    useEffect(() => {
        fetchCountryCodes({ onSuccess: (list) => setCountryCodes(list) });

        return () => {
            dispatch(closeCommentsThread());
        };
    }, []);

    useEffect(() => {
        if (originalCategory) dispatch(closeCommentsThread());
    }, [originalCategory]);

    // Callback to update selectedOrgUsers from child components
    const handleUsersUpdate = (updatedUsers: OrgUser[]) => {
        setSelectedOrgUsers(updatedUsers);
    };

    const handleDrawerClose = (shouldRefetch?: boolean) => {
        dispatch(setShowManageUserDrawer(false));
        params.delete('orgId');
        setParams(params);
        if (shouldRefetch) {
            fetchRows(Number(params.get('page')) || 1);
        }
    };

    const handleDealDrawerClose = () => {
        fetchRows(Number(params.get('page')) || 1);
        params.delete('orgId');
        setParams(params);
    };

    const fetchOwnerDetails = () => {
        let payload: GetOwnerDataPayload = {
            investorOrgId: user.userData.investorOrgId,
        };
        dispatch(fetchOwners(payload));
    };

    useEffect(() => {
        fetchOwnerDetails();
    }, []);

    const prepareOwnerDropdownData = () => {
        const ownersData = {};
        owners.forEach((owner) => {
            if (!owner.name) return;
            ownersData[owner.userId] = {
                label: owner.name,
                value: owner.userId,
                backgroundColor: COLORS.LIGHT_BG,
                labelColor: COLORS.BLACK,
                customCaret: GREY_DROPDOWN_CARET,
            };
        });
        return ownersData;
    };

    useEffect(() => {
        if (owners.length > 0) setOwnerDropdownData(prepareOwnerDropdownData());
    }, [owners]);

    const updateOwner = (orgId: string, owner: any) => {
        setLocalOwners((prev) => ({
            ...prev,
            [orgId]: owner.userId,
        }));

        // If clearing selection (owner.value is empty), add to cleared set
        if (isEmpty(owner.userId)) {
            setClearedOrganizations((prev) => new Set(prev).add(orgId));
        } else {
            // If selecting an owner, remove from cleared set
            setClearedOrganizations((prev) => {
                const newSet = new Set(prev);
                newSet.delete(orgId);
                return newSet;
            });
        }

        dispatch(
            updateCompany({
                orgId: orgId,
                owner: owner,
            }),
        );
    };

    const getOwnerDropdownOption = (orgId: string, userId: string): any => {
        // Check if this organization was cleared first
        if (clearedOrganizations.has(orgId)) {
            return {
                label: 'Select',
                value: '-',
                backgroundColor: '#F5F5F5',
                labelColor: '#B4B4B4',
                customCaret: GREY_DROPDOWN_CARET,
            };
        }

        // Check if we have a local override first
        const localOwnerId = localOwners[orgId];
        if (localOwnerId && ownerDropdownData[localOwnerId]) {
            return ownerDropdownData[localOwnerId];
        }

        // If we have a direct match for the orgId in ownerDropdownData, use it
        if (ownerDropdownData[userId]) {
            return ownerDropdownData[userId];
        }

        // Fallback to default option
        return {
            label: 'Select',
            value: '-',
            backgroundColor: '#F5F5F5',
            labelColor: '#B4B4B4',
            customCaret: GREY_DROPDOWN_CARET,
        };
    };

    const getSelectedOption = ({ row, value, headerId, getOwnerDropdownOptionById, getOwnerDropdownOption }) => {
        if (localOwners?.[row?.organizationId] || localOwners?.[row?.organizationId] === '') {
            return getOwnerDropdownOptionById(localOwners[row?.organizationId]);
        }
        return getOwnerDropdownOption?.(row?.owners?.[headerId]) ?? getOwnerDropdownOptionById(value);
    };

    const onCommentsClick = (row) => {
        if (commentsThreadState.showCommentsThread) {
            if (
                commentsThreadState.source === COMMENTS_THREAD_SOURCES.MANAGE_PARTNERS_LENDERS &&
                commentsThreadState.organizationId === row.organizationId
            ) {
                dispatch(closeCommentsThread());
            }
        }
        dispatch(
            setShowCommentsThread({
                source: COMMENTS_THREAD_SOURCES.MANAGE_PARTNERS_LENDERS,
                organizationId: row.organizationId,
                headerText: row.organizationName,
            }),
        );
    };

    const onMouseEnterComment = (e, text) => {
        e.stopPropagation();
        e.preventDefault();
        dispatch(
            setTooltipContent({
                content: text,
                target: e.currentTarget,
            }),
        );
    };

    const onMouseLeaveComment = () => {
        removeTooltipContent(dispatch);
    };

    const getRowText = (row: OrgBasedUser, header: { id: number; label: string }) => {
        if (isRowsLoading) return '';

        const labelMap: Record<string, () => React.ReactNode> = {
            [`${category} Name`]: () => <div title={row?.organizationDisplayName || '-'}>{row?.organizationDisplayName || '-'}</div>,

            'Profile Type': () => <div title={row?.organizationType || '-'}>{row?.organizationType || '-'}</div>,

            Users: () => {
                if (!row?.userData || row.userData.length === 0) return '-';
                const userCount = row.userData.length;
                const firstUser = row.userData?.[0];

                return (
                    <div className={styles.UsersCell} role="button" title={row.userData.map((user) => user.name).join(`, \n`)}>
                        <span className={styles.FirstUser}>{firstUser?.name || '-'}</span>
                        {userCount > 1 && <span className={styles.RemainingCount}>{`, +${userCount - 1} more`}</span>}
                    </div>
                );
            },
            [`${category} Owner`]: () => (
                <div
                    style={{
                        position: 'relative',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '40%',
                    }}
                    title={ownerDropdownData[row?.organizationOwner]?.label || 'Select'}
                >
                    <StatusDropdown
                        dropdownOptions={Object.values(
                            ownerDropdownData ?? {
                                label: 'Select',
                                value: '-',
                                backgroundColor: '#F5F5F5',
                                labelColor: '#B4B4B4',
                                customCaret: GREY_DROPDOWN_CARET,
                            },
                        )}
                        selectedOption={getOwnerDropdownOption(row.organizationId, row?.organizationOwner)}
                        selectOption={(option: StatusDropdownOption) =>
                            updateOwner(row.organizationId, {
                                name: option.label,
                                userId: option.value,
                                activityBy: user.userData.userName,
                                activityDate: new Date().toISOString(),
                                ownerType: 'owner',
                            })
                        }
                        disabled={isSummonUser}
                    />
                </div>
            ),
            'Internal Comments': () => (
                <div
                    className={styles.CommentsBox}
                    onClick={(e) => {
                        e.stopPropagation();
                        onCommentsClick(row);
                    }}
                >
                    {!row?.comment ? (
                        <div className={styles.AddComment}>Add comment</div>
                    ) : (
                        <div
                            className={styles.Comment}
                            onMouseEnter={(e) => onMouseEnterComment(e, row?.comment?.text)}
                            onMouseLeave={onMouseLeaveComment}
                        >
                            {truncateTextForComments(row?.comment?.text, true)}
                            <div className={styles.Date}>
                                {row?.comment?.activityDate &&
                                    `${dayjs(row?.comment?.activityDate).format('hh:mm A, DD MMM YYYY')} by ${row?.comment?.activityBy}`}
                            </div>
                            {}
                        </div>
                    )}
                </div>
            ),
            'Investment Pref.': () => (
                <div className={styles.InvestmentPrefCount}>
                    {`${row?.investmentPreferencesCount ? row?.investmentPreferencesCount : 'Not'} active`}
                </div>
            ),
            'Fee Aligned': () => <div>{row?.orgAdditionalDetails?.feeAligned ? `${row?.orgAdditionalDetails?.feeAligned}%` : '-'}</div>,
            Actions: () => (
                <div>
                    <ActionMenu
                        triggerPayload={{
                            organizationId: row?.organizationId,
                            organizationName: row?.organizationName,
                            selectedUsers: row?.userData,
                            userId: row?.userData?.[0]?.userId,
                            userType: row?.userData?.[0]?.userType,
                            organizationDisplayName: row?.organizationDisplayName,
                        }}
                        rowData={{
                            mainConfig: {
                                actionConfig,
                            },
                        }}
                        actionMenu={{
                            ...actionMenuItems,
                            'Manage User': {
                                action: (payload) => {
                                    const heading = payload?.organizationDisplayName
                                        ? payload?.organizationDisplayName
                                        : payload?.organizationName;
                                    handleManageUsers(payload?.selectedUsers, payload?.organizationId, heading);
                                },
                            },
                        }}
                        source="MANAGE_USERS"
                        disabled={isSummonUser}
                        usePortal={true}
                    />
                </div>
            ),
        };

        return labelMap?.[header.label]?.() ?? '-';
    };

    const fetchConfig = async () => {
        try {
            await getDealFormConfig(
                {
                    accountType: userData.accountType,
                    investorOrgId: platformData?.investorOrgId,
                    primaryEmail: userData?.primaryEmail,
                    configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
                },
                {
                    onSuccess: (configData) => {
                        setManageLendersColumn(configData?.manageLendersColumns ?? { columns: [] });
                        setManagePartnersColumn(configData?.managePartnersColumns ?? { columns: [] });
                    },
                    onError: () => {
                        console.error('Error fetching leads columns config');
                    },
                },
            );
        } catch (error) {
            console.error('Error fetching leads columns config:', error);
        }
    };

    useEffect(() => {
        if (category) fetchConfig();
    }, [category, platformData]);

    const fetchSectors = () => {
        fetchSectorOptions({ onSuccess: (sectors) => setSectorDropdownOptions(sectors) });
    };

    useEffect(() => {
        fetchSectors();
    }, []);

    const config = category === 'Lender' ? manageLendersColumn : managePartnersColumn;

    const sectorOptionsMap = sectorDropdownOptions.reduce<Record<string, { label: string; value: string }>>(
        (acc, s) => ({ ...acc, [s.value]: s }),
        {},
    );

    const enrichColumn = (col: any, index: number): any => {
        switch (col.field) {
            case 'organizationDisplayName':
            case 'organizationName':
                return index === 0 ? { ...col, type: DataTableColumnType.COMPANY_NAME_CTA } : col;
            case 'customFields.sectoralPreference':
                return sectorDropdownOptions.length > 0 ? { ...col, dropdownOptions: sectorOptionsMap } : col;
            default:
                return col;
        }
    };

    const mapColumnsType = (columns: any[]): any[] =>
        columns.map((col) => ({
            ...col,
            type: DataTableColumnType[col.type as keyof typeof DataTableColumnType] || col.type,
        }));

    const columns = mapColumnsType(config.columns || []).map((col, index) => enrichColumn(col, index));

    const pinnedColumns =
        'pinnedColumns' in config && Array.isArray((config as any).pinnedColumns) ? (config as any).pinnedColumns : undefined;

    // The standalone mobile drawer has no DataTable to apply the ViewConfig `show` gate for it.
    const filterDrawerColumns = getVisibleColumns(columns);

    return {
        // States
        manageUserDrawerState,
        selectedOrgUsers,
        selectedOrgName,
        showDealDrawer,
        dealDrawerState,
        isFilterDrawerOpen,

        // Constants
        originalCategory,

        // Columns
        columns,
        pinnedColumns,
        filterDrawerColumns,

        // Methods
        handleRowClick,
        handleManageUsers,
        handleDrawerClose,
        getRowText,
        handleDealDrawerClose,
        commentsThreadState,
        updateOwner,
        localOwners,
        getSelectedOption,
        handleUsersUpdate,
    };
};
